// Copyright (c) Ping. All rights reserved.
//
// Web shim for `react-native-linear-gradient`. The upstream package is
// a native module that has no browser implementation; importing it via
// react-native-web produces a `null` component (same failure mode as
// `react-native-svg` before we aliased that one). On web we render a
// plain `<div>` with a CSS `linear-gradient(...)` `backgroundImage`,
// which is what the native side resolves to under the hood anyway.
//
// Wired in `vite.config.ts` under `resolve.alias` for
// `^react-native-linear-gradient$`.
//
// Prop surface matches the upstream `LinearGradient`:
//   - `colors: string[]`               required
//   - `locations?: number[]`           optional stops in 0..1
//   - `start?: { x, y }`               default { x: 0.5, y: 0 }
//   - `end?: { x, y }`                 default { x: 0.5, y: 1 }
//   - `useAngle?: boolean`             default false; if true the
//                                      `angle` prop drives the gradient
//   - `angle?: number`                 degrees, 0 = top
//   - `style?` + standard children     forwarded to the wrapping div

import type { CSSProperties, PropsWithChildren } from 'react';

interface Point {
  readonly x: number;
  readonly y: number;
}

export interface LinearGradientProps {
  readonly colors: readonly (string | number)[];
  readonly locations?: readonly number[];
  readonly start?: Point;
  readonly end?: Point;
  readonly useAngle?: boolean;
  readonly angle?: number;
  readonly style?: unknown;
  readonly className?: string;
}

function toCssColor(c: string | number): string {
  if (typeof c === 'number') {
    // RN often passes hex-as-int (0xAARRGGBB or 0xRRGGBB); coerce.
    const hex = c.toString(16).padStart(6, '0');
    return `#${hex}`;
  }
  return c;
}

function buildStops(
  colors: readonly (string | number)[],
  locations: readonly number[] | undefined,
): string {
  if (locations && locations.length === colors.length) {
    return colors.map((c, i) => `${toCssColor(c)} ${(locations[i] ?? 0) * 100}%`).join(', ');
  }
  return colors.map(toCssColor).join(', ');
}

function buildDirection(
  start: Point | undefined,
  end: Point | undefined,
  useAngle: boolean | undefined,
  angle: number | undefined,
): string {
  if (useAngle && typeof angle === 'number') {
    return `${angle}deg`;
  }
  const s = start ?? { x: 0.5, y: 0 };
  const e = end ?? { x: 0.5, y: 1 };
  // Translate the start/end vector to an angle in degrees. RN's
  // coordinate space has y growing downward, same as CSS, so the
  // mapping is direct. 0deg in CSS points up; rotate by 90.
  const dx = e.x - s.x;
  const dy = e.y - s.y;
  const rad = Math.atan2(dy, dx);
  const deg = (rad * 180) / Math.PI + 90;
  return `${deg}deg`;
}

export function LinearGradient(props: PropsWithChildren<LinearGradientProps>): JSX.Element {
  const { colors, locations, start, end, useAngle, angle, style, className, children } = props;

  const direction = buildDirection(start, end, useAngle, angle);
  const stops = buildStops(colors, locations);
  const backgroundImage = `linear-gradient(${direction}, ${stops})`;

  // RN style objects flatten 1:1 into HTML inline styles via
  // react-native-web's StyleSheet runtime; passing it through unchanged
  // works for the simple case where consumers pass a flat style object.
  const mergedStyle: CSSProperties = {
    ...((style as CSSProperties | undefined) ?? {}),
    backgroundImage,
  };

  return (
    <div style={mergedStyle} className={className}>
      {children}
    </div>
  );
}

export default LinearGradient;
